import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  Send,
  Plus,
  MessageSquare,
  Trash2,
  Loader2,
  ArrowRight,
  X,
  Edit2,
  Check,
  Users,
  Link,
  Sparkles,
} from 'lucide-react';
import chatService, { ChatSession, Message, ChatSource } from '../services/chatService';
import tenantService, { OnboardingStatus } from '../services/tenantService';
import employeeService, { Employee } from '../services/employeeService';
import { useAuth } from '../contexts/AuthContext';
import SourcesPanel from '../components/SourcesPanel';
import ChatMessage from '../components/ChatMessage';

/** Employee initials avatar — 2-letter monogram in a rounded square */
function EmployeeAvatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || 'N';
  const cls = size === 'sm'
    ? 'w-7 h-7 text-xs rounded-md'
    : 'w-10 h-10 text-sm rounded-xl';
  return (
    <div className={`${cls} bg-niwt-primary flex items-center justify-center flex-shrink-0`}>
      <span className="text-white font-semibold">{initials}</span>
    </div>
  );
}

/** Relative date label for session list */
function formatSessionDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedTwin, setSelectedTwin] = useState<string>('');
  const [selectedSession, setSelectedSession] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [creatingSession, setCreatingSession] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Sources panel state
  const [selectedMessageIndex, setSelectedMessageIndex] = useState<number | null>(null);
  const [sourcesDrawerOpen, setSourcesDrawerOpen] = useState(false);

  // Onboarding state
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);

  // Track if we're in the initial restoration phase to prevent race conditions
  const restorationCompleteRef = useRef(false);

  // Derived: sources for the selected message
  const selectedSources: ChatSource[] =
    selectedMessageIndex !== null && messages[selectedMessageIndex]?.sources
      ? messages[selectedMessageIndex].sources!
      : [];

  // Derived: selected employee info for display
  const selectedEmployee = employees.find(e => e.email === selectedTwin);
  const selectedEmployeeName = selectedEmployee?.displayName
    || (selectedTwin ? selectedTwin.split('@')[0] : '');

  // Initialize: check onboarding, load employees, restore session — all in one pass
  useEffect(() => {
    if (!user || !user.email) return;

    const initialize = async () => {
      try {
        // Parallel fetch: tenant status + employees (saves ~10s vs serial on cold start)
        const [tenant, empResult] = await Promise.all([
          tenantService.getTenant(),
          employeeService.listEmployees({ limit: 50 }).catch(() => ({ employees: [] as Employee[] })),
        ]);

        setOnboarding(tenant.onboardingStatus);

        if (tenant.onboardingStatus.connected && tenant.onboardingStatus.employeesSelected) {
          const emps = empResult.employees || [];
          setEmployees(emps);

          // Check for pending twin selection from Niwt Directory
          const pendingTwin = localStorage.getItem('pending_twin_selection');
          if (pendingTwin) {
            localStorage.removeItem('pending_twin_selection');
          }

          // Priority: pending selection > last used > first employee
          const lastTwin = localStorage.getItem(`last_selected_twin_${user.email}`);
          const validTwin =
            (pendingTwin && emps.some(e => e.email === pendingTwin)) ? pendingTwin
            : (lastTwin && emps.some(e => e.email === lastTwin)) ? lastTwin
            : emps.length > 0 ? emps[0].email : user.email;
          setSelectedTwin(validTwin);

          // Restore sessions for selected twin
          try {
            const sessionsData = await chatService.getSessions(validTwin);
            setSessions(sessionsData);

            const lastSession = localStorage.getItem(`last_selected_session_${user.email}`);
            if (lastSession && sessionsData.find(s => s.session_id === lastSession)) {
              setSelectedSession(lastSession);
            } else if (lastSession) {
              localStorage.removeItem(`last_selected_session_${user.email}`);
            }
          } catch (err) {
            console.error('Error loading sessions:', err);
          }
        }
      } catch (err) {
        setOnboarding({ connected: false, employeesSelected: false });
      } finally {
        setLoading(false);
        restorationCompleteRef.current = true;
      }
    };
    initialize();
  }, [user]);

  // Reload sessions when user changes twin (post-initialization only)
  useEffect(() => {
    if (selectedTwin && restorationCompleteRef.current) {
      loadSessions(selectedTwin);
      if (user && user.email) {
        localStorage.setItem(`last_selected_twin_${user.email}`, selectedTwin);
      }
    }
  }, [selectedTwin]);

  // Load messages when session is selected
  useEffect(() => {
    if (selectedSession) {
      loadMessages(selectedSession);
      if (user && user.email) {
        localStorage.setItem(`last_selected_session_${user.email}`, selectedSession);
      }
    }
  }, [selectedSession]);

  // Auto scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const loadSessions = async (twinId: string) => {
    if (!user || !user.email) return;
    try {
      const sessionsData = await chatService.getSessions(twinId, user.email);
      setSessions(sessionsData);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadMessages = async (sessionId: string) => {
    try {
      setLoadingMessages(true);
      setSelectedMessageIndex(null);
      const history = await chatService.getSessionHistory(sessionId);
      setMessages(history.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const createNewSession = async () => {
    if (!selectedTwin || creatingSession) return;
    setCreatingSession(true);
    try {
      const sessionTitle = selectedEmployeeName ? `Chat with Niwt ${selectedEmployeeName}` : 'New conversation';
      const newSession = await chatService.createSession(selectedTwin, sessionTitle);
      await loadSessions(selectedTwin);
      setSelectedSession(newSession.session_id);
      setMessages([]);
    } catch (error) {
      console.error('Failed to create session:', error);
      const fallbackSession: ChatSession = {
        session_id: `local_${Date.now()}`,
        title: selectedEmployeeName ? `Chat with Niwt ${selectedEmployeeName}` : 'New conversation',
        twin_user_id: selectedTwin || 'default',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setSessions(prev => [...prev, fallbackSession]);
      setSelectedSession(fallbackSession.session_id);
      setMessages([]);
    } finally {
      setCreatingSession(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !selectedSession || sending) return;

    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage;
    setInputMessage('');
    setSending(true);

    try {
      const response = await chatService.sendMessage(selectedSession, currentInput);
      setMessages(prev => {
        const updated = [...prev, response];
        if (response.sources && response.sources.length > 0) {
          setSelectedMessageIndex(updated.length - 1);
        } else {
          setSelectedMessageIndex(null); // Clear stale sources when new response has none
        }
        return updated;
      });

      // Reload session list after first message so AI-generated title (from backend) appears
      if (messages.length === 0) {
        loadSessions(selectedTwin).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const deleteSession = async (sessionId: string) => {
    try {
      await chatService.deleteSession(sessionId);
      await loadSessions(selectedTwin);
      if (selectedSession === sessionId) {
        setSelectedSession('');
        setMessages([]);
        if (user && user.email) {
          localStorage.removeItem(`last_selected_session_${user.email}`);
        }
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (selectedSession === sessionId) {
        setSelectedSession('');
        setMessages([]);
        if (user && user.email) {
          localStorage.removeItem(`last_selected_session_${user.email}`);
        }
      }
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedSessionIds);
    const wasCurrentDeleted = selectedSessionIds.has(selectedSession);
    await Promise.all(ids.map(id => chatService.deleteSession(id).catch(() => {})));
    setSelectedSessionIds(new Set());
    setSelectMode(false);
    await loadSessions(selectedTwin);
    if (wasCurrentDeleted) {
      setSelectedSession('');
      setMessages([]);
      if (user && user.email) {
        localStorage.removeItem(`last_selected_session_${user.email}`);
      }
    }
  };

  const startEditingSession = (sessionId: string, currentTitle: string) => {
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  };

  const cancelEditing = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const saveSessionTitle = async (sessionId: string) => {
    if (!editingTitle.trim()) return;
    try {
      await chatService.updateSessionTitle(sessionId, editingTitle.trim());
      await loadSessions(selectedTwin);
      setEditingSessionId(null);
      setEditingTitle('');
    } catch (error) {
      console.error('Failed to update session title:', error);
    }
  };

  const handleEditKeyPress = (e: React.KeyboardEvent, sessionId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveSessionTitle(sessionId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-niwt-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading chat interface...</p>
        </div>
      </div>
    );
  }

  // Onboarding gate: not connected
  if (onboarding && !onboarding.connected) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Link className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 font-libre mb-2">Connect Your Data Source</h2>
          <p className="text-gray-600 mb-6">
            To start chatting with your AI assistant, first connect your Microsoft 365 account.
          </p>
          <button
            onClick={() => navigate('/integrations')}
            className="inline-flex items-center px-6 py-3 bg-niwt-primary text-white font-medium rounded-lg hover:bg-niwt-secondary transition-colors"
          >
            Go to Integrations
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    );
  }

  // Onboarding gate: connected but no employees selected
  if (onboarding && onboarding.connected && !onboarding.employeesSelected) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-niwt-primary" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 font-libre mb-2">Select Employees</h2>
          <p className="text-gray-600 mb-6">
            Your Microsoft 365 account is connected. Now select employees from your directory to create digital twins.
          </p>
          <button
            onClick={() => navigate('/employee-selection')}
            className="inline-flex items-center px-6 py-3 bg-niwt-primary text-white font-medium rounded-lg hover:bg-niwt-secondary transition-colors"
          >
            Select Employees
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="h-[calc(100vh-104px)] grid grid-cols-12 gap-4">

        {/* ── Sidebar ── */}
        <aside className="col-span-12 lg:col-span-3 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">

          {/* Compact header with twin selector */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Conversations</h2>
              <div className="flex items-center gap-2">
                {sessions.length > 0 && (
                  <button
                    onClick={() => { setSelectMode(!selectMode); setSelectedSessionIds(new Set()); }}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {selectMode ? 'Cancel' : 'Select'}
                  </button>
                )}
                {!selectMode && (
                  <button
                    title="New conversation"
                    onClick={createNewSession}
                    disabled={!selectedTwin}
                    className="p-1.5 bg-niwt-primary text-white rounded-lg hover:bg-niwt-secondary transition-colors disabled:opacity-40"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Twin selector — only shown when multiple employees */}
            {employees.length > 1 && (
              <select
                value={selectedTwin}
                onChange={(e) => setSelectedTwin(e.target.value)}
                className="mt-2 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent"
              >
                {employees.map(emp => (
                  <option key={emp.employee_id} value={emp.email}>
                    Niwt {emp.displayName}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Session list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {sessions.length === 0 ? (
              <div className="text-center py-10">
                <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No conversations yet</p>
                <p className="text-xs text-gray-300 mt-0.5">Start a new chat to begin</p>
              </div>
            ) : selectMode ? (
              /* ── Checkbox select mode ── */
              sessions.map(session => {
                const isChecked = selectedSessionIds.has(session.session_id);
                return (
                  <div
                    key={session.session_id}
                    className={`px-3 py-2.5 rounded-lg transition-colors cursor-pointer flex items-center gap-2.5 ${
                      isChecked ? 'bg-purple-50 border border-purple-200' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                    onClick={() => {
                      setSelectedSessionIds(prev => {
                        const next = new Set(prev);
                        if (next.has(session.session_id)) next.delete(session.session_id);
                        else next.add(session.session_id);
                        return next;
                      });
                    }}
                  >
                    {/* Checkbox */}
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isChecked ? 'border-niwt-primary bg-niwt-primary' : 'border-gray-300'
                    }`}>
                      {isChecked && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate text-gray-700">{session.title}</p>
                      <p className="text-xs text-gray-400">{formatSessionDate(session.updated_at)}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              /* ── Normal mode ── */
              sessions.map(session => (
                <div
                  key={session.session_id}
                  className={`px-3 py-2.5 rounded-lg transition-colors group ${
                    selectedSession === session.session_id
                      ? 'bg-purple-50 border border-purple-200'
                      : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between gap-1">
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => setSelectedSession(session.session_id)}
                    >
                      {editingSessionId === session.session_id ? (
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onKeyDown={(e) => handleEditKeyPress(e, session.session_id)}
                          onBlur={() => saveSessionTitle(session.session_id)}
                          className="w-full text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <p
                          className={`text-sm truncate ${
                            selectedSession === session.session_id ? 'font-medium text-gray-900' : 'text-gray-700'
                          }`}
                          onDoubleClick={(e) => { e.stopPropagation(); startEditingSession(session.session_id, session.title); }}
                          title="Double-click to rename"
                        >
                          {session.title}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatSessionDate(session.updated_at)}
                      </p>
                    </div>

                    {/* Session actions */}
                    <div className="flex items-center gap-0.5 flex-shrink-0 mt-0.5">
                      {editingSessionId === session.session_id ? (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); saveSessionTitle(session.session_id); }}
                            className="p-1 text-green-600 hover:text-green-700 transition-colors"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); cancelEditing(); }}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : deletingSessionId === session.session_id ? (
                        /* Inline delete confirmation — no window.confirm */
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <span className="text-xs text-red-500 font-medium">Delete?</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteSession(session.session_id); setDeletingSessionId(null); }}
                            className="p-1 text-red-600 hover:text-red-700 transition-colors"
                            title="Confirm delete"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeletingSessionId(null); }}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Cancel"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); startEditingSession(session.session_id, session.title); }}
                            className="opacity-30 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 transition-all"
                            title="Rename"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeletingSessionId(session.session_id); }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Bulk delete bar — shown when items are selected */}
          {selectMode && selectedSessionIds.size > 0 && (
            <div className="px-3 pb-3 flex-shrink-0">
              <button
                onClick={deleteSelected}
                className="w-full py-2 bg-red-50 text-red-600 text-sm font-medium rounded-lg hover:bg-red-100 transition-colors"
              >
                Delete {selectedSessionIds.size} {selectedSessionIds.size === 1 ? 'conversation' : 'conversations'}
              </button>
            </div>
          )}
        </aside>

        {/* ── Main Chat + Sources ── */}
        <main className="col-span-12 lg:col-span-9 bg-white rounded-xl shadow-sm border border-gray-200 flex overflow-hidden">

          {/* Chat column */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

            {/* Chat Header — employee identity, no redundant "Talking to:" */}
            <header className="px-5 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                {selectedEmployeeName ? (
                  <EmployeeAvatar name={selectedEmployeeName} />
                ) : (
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-niwt-primary" />
                  </div>
                )}
                <div>
                  <h1 className="text-lg font-bold text-gray-900 font-libre leading-tight">
                    {selectedEmployeeName ? `Niwt ${selectedEmployeeName}` : 'Select a Niwt'}
                  </h1>
                  {selectedEmployee?.jobTitle && (
                    <p className="text-sm text-gray-500 leading-tight">{selectedEmployee.jobTitle}</p>
                  )}
                </div>
              </div>
            </header>

            {/* Messages Area */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-white">
              {!selectedSession ? (
                <div className="flex items-center justify-center h-full">
                  <button
                    type="button"
                    disabled={!selectedTwin || creatingSession}
                    onClick={createNewSession}
                    className="text-center group disabled:cursor-default enabled:cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-niwt-primary focus-visible:ring-offset-2 rounded-2xl"
                  >
                    <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-enabled:group-hover:bg-purple-100 transition-colors">
                      <Bot className="w-8 h-8 text-purple-300 group-enabled:group-hover:text-niwt-primary transition-colors" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Start a conversation</h3>
                    <p className="text-sm text-gray-500">
                      {creatingSession
                        ? 'Starting...'
                        : selectedTwin
                          ? 'Click to start a new conversation'
                          : 'Select or create a chat session to begin'}
                    </p>
                  </button>
                </div>
              ) : loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-niwt-primary" />
                </div>
              ) : (
                <>
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`px-4 py-3 rounded-2xl ${
                          message.role === 'user'
                            ? 'max-w-lg lg:max-w-xl bg-niwt-primary text-white'
                            : `w-full bg-white border text-gray-900 shadow-sm ${
                                selectedMessageIndex === index
                                  ? 'border-purple-300 ring-1 ring-purple-200'
                                  : 'border-gray-200'
                              }`
                        }`}
                      >
                        <ChatMessage content={message.content} isUser={message.role === 'user'} />

                        {/* Sources indicator — pill chip, always visible */}
                        {message.role === 'assistant' && message.sources && message.sources.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => {
                                const newIndex = selectedMessageIndex === index ? null : index;
                                setSelectedMessageIndex(newIndex);
                                if (newIndex !== null && window.innerWidth < 1024) {
                                  setSourcesDrawerOpen(true);
                                }
                              }}
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                selectedMessageIndex === index
                                  ? 'bg-purple-100 text-niwt-primary'
                                  : 'bg-gray-100 text-gray-500 hover:bg-purple-50 hover:text-niwt-primary'
                              }`}
                            >
                              <Sparkles className="w-3 h-3" />
                              {message.sources.length} {message.sources.length === 1 ? 'source' : 'sources'}
                            </button>
                          </div>
                        )}

                        {message.timestamp && (
                          <p className={`text-xs mt-1 ${
                            message.role === 'user' ? 'text-purple-200' : 'text-gray-400'
                          }`}>
                            {new Date(message.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Thinking state — calm, single animation */}
                  {sending && (
                    <div className="flex justify-start">
                      <div className="px-4 py-3 rounded-2xl bg-white border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-niwt-primary" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              Reviewing {selectedEmployeeName ? `${selectedEmployeeName}'s` : ''} knowledge
                            </p>
                            <div className="flex gap-1 mt-1.5">
                              <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Area */}
            {selectedSession && (
              <footer className="px-5 py-4 border-t border-gray-200">
                <div className="flex items-center gap-3">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={`Ask Niwt ${selectedEmployeeName || 'a question'}...`}
                    rows={1}
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent resize-none bg-gray-50 placeholder:text-gray-400"
                    disabled={sending}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || sending}
                    className="p-3 bg-niwt-primary text-white rounded-xl hover:bg-niwt-secondary focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:ring-offset-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </footer>
            )}
          </div>

          {/* Sources Panel — desktop (flex sibling, slides in from right) */}
          {selectedSources.length > 0 && selectedMessageIndex !== null && (
            <div className="hidden lg:block w-72 xl:w-80 flex-shrink-0 border-l border-gray-200 animate-slideIn">
              <SourcesPanel
                sources={selectedSources}
                isVisible={true}
                onClose={() => setSelectedMessageIndex(null)}
              />
            </div>
          )}
        </main>

        {/* Sources Panel — mobile drawer */}
        <SourcesPanel
          sources={selectedSources}
          isVisible={sourcesDrawerOpen}
          isMobileDrawer={true}
          onClose={() => { setSourcesDrawerOpen(false); setSelectedMessageIndex(null); }}
        />
      </div>
    </div>
  );
};

export default Chat;
